import React, { useEffect, useState, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Container, Grid } from '@mui/material';
import TitleTitleBar from './TitleDetailTitleBar';
import { clearTitle, getTitleDetails, getTitleVersions, setActiveTitle } from '@/redux/slices/titleSlice';
import { selectTitle, selectTitleLoading } from '@/redux/selectors/titleSelectors';
import { selectTmdbTitle } from '@/_utils/titleUtils';
import baseAxios from 'axios';
import { tmdb_multi_search } from '@/constants/constants';
import TitleDetailTabs from './TitleDetailTabs';
import TitleDetailHeader from './TitleDetailHeader';
import { insertWordBreaks, checkIfUrlIsValid } from '@/_utils/utils';
import Poster from '@/components/_common/Poster';
import { useQuery } from '@/components/_hooks/useQuery';
import { ConfigContext } from '@/contextAPI/configContext';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import TitleDetailV2 from '@/components/TitleDetail/v2/TitleDetailV2';

const TitleDetail = () => {
  const v2 = useContext(ConfigContext)?.titleConfig?.v2;
  if (v2) return <TitleDetailV2 />;

  const dispatch = useAppDispatch();
  const history = useHistory();
  const title = useAppSelector(selectTitle);
  const disableTMDB = useContext(ConfigContext)?.titleConfig?.disableTMDB;
  const [loading, setLoading] = useState(false);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const loadingTitle = useAppSelector(selectTitleLoading);
  const { id } = useParams<{ id: string }>();
  const query = useQuery();
  const seasonId = query.get('season_id');
  const episodeId = query.get('episode_id');
  const supplementalId = query.get('supplemental_id');
  const activeTitleId = Number(supplementalId || episodeId ||seasonId || id);

  useEffect(() => {
    dispatch(setActiveTitle(activeTitleId));
  }, [activeTitleId]);

  useEffect(() => {
    dispatch(getTitleVersions(+id));
    const promise = dispatch(getTitleDetails(+id));
    promise.unwrap().catch((error) => {
      if (error.name === 'AbortError') return;
      history.push('/page-not-found');
    });
    return () => {
      promise.abort();
      dispatch(clearTitle());
    };
  }, [id]);

  useEffect(() => {
    const retrieveThumbnail = async () => {
      let workingUrl = null;

      for (const url of [title?.cover_art_url, title?.thumbnail_link]) {
        if (url) {
          const isValid = await checkIfUrlIsValid(url);
          if (isValid) {
            workingUrl = url;
            break;
          }
        }
      }

      if (workingUrl) {
        setThumbnail(workingUrl);
      } else {
        if (disableTMDB) {
          setThumbnail(null);
          setLoading(false);
        } else if (title?.id) {
          fetchTmdb();
        }
      }
    };

    retrieveThumbnail();

    return () => {
      setThumbnail(null);
      setLoading(false);
    };
  }, [title?.id]);

  const fetchTmdb = () => {
    if (!title) return;

    const { lineage_meta, name, entity_type } = title;
    const year = title?.meta?.characteristics?.release_year;
    setLoading(true);
    baseAxios
      .get(tmdb_multi_search(lineage_meta?.series_name || name, entity_type, year))
      .then((res) => {
        // if we got no results searching including year, search without bc sometimes we have the wrong year
        if (!res.data?.results?.length && year) {
          return baseAxios.get(tmdb_multi_search(lineage_meta?.series_name || name, entity_type));
        } else return res;
      })
      .then((res) => {
        const selectedTitle = selectTmdbTitle(res.data?.results);
        if (selectedTitle) {
          setThumbnail(`https://image.tmdb.org/t/p/original/${selectedTitle.poster_path}`);
        }
      })
      .catch(() => console.error('could not fetch tmdb data'))
      .then(() => setLoading(false));
  };

  return (
    <>
      <TitleTitleBar />

      <Container>
        <Grid container spacing={5}>
          {/* POSTER */}
          <Grid item xs={6} lg={3} sx={{ display: { xs: 'none', lg: 'block' } }}>
            <Poster loading={loading || loadingTitle} src={thumbnail ?? undefined} title={title ? insertWordBreaks(title.name) : undefined} />
          </Grid>

          {/* TITLE HEADER */}
          <Grid item xs={12} lg={9}>
            <TitleDetailHeader />
            {/* METADATA/INVENTORY TABS */}
            <TitleDetailTabs />
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default TitleDetail;
